import { useState, type ReactNode } from 'react';
import { CornerDownRight, MessageCircleQuestion } from 'lucide-react';
import {
  answersComplete,
  backlogAnswerNote,
  formatFactoryAnswers,
  formatQuestionsReply,
  isSkippedAnswer,
  QUESTIONS_REPLY_MAX,
  type FactoryAnswer,
  type FactoryQuestion,
  type FactoryQuestions,
} from '@shared/factory-questions';
import { ownWordsNote, proposalAnswer, sendReplyRead, type UpstreamProposal } from '@shared/upstream-deferral';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { formatNumber } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { FoldingCard } from './FoldingCard';
import { SendUpstreamButton, SentUpstreamNote, type UpstreamTagging } from './SendUpstreamButton';
import type { ConcernTags } from '@shared/decision-concern';
import type { BacklogOutcome } from '@shared/factory-backlog';
import { TASK_CHAT_MAX } from '@shared/types/tasks';
import { ConcernBadge } from './ConcernBadge';
import { DecisionItemInput } from './DecisionItemInput';

/**
 * T-58: the round's questions out with the task's source, and Jev's picks from the replies there
 * (pre-selected). `onSend` (a mirrored task, someone who can comment): the Send to <source> button.
 */
export interface QuestionsUpstream {
  sourceLabel: string;
  sent: ReadonlyMap<number, unknown>;
  proposals: ReadonlyMap<number, UpstreamProposal>;
  /** The task still waits on its source; each sent item checks its own send for a reply read. */
  waiting?: boolean;
  onSend?: (numbers: number[], mentionId: string | null) => Promise<boolean>;
  /** SF-T-101: who the send can tag. */
  tagging?: UpstreamTagging;
}

/**
 * T-10: the coding agent's questions on a task (a `factory-questions` block in its message), as a
 * card. While they're open, someone who can comment picks options or writes an answer and sends them
 * as one chat message, which goes back to the same agent. Once someone has replied, or without
 * `onAnswer`, it shows the questions and their options, or with `answers` (T-22) each question's answer.
 * T-58: with `upstream`, questions sent to the task's source say so, and the answers picked from the
 * replies there start selected.
 * SF-T-163: a plain comment no longer closes the card, so what's picked here stays while people chat.
 * Reply in words opens a box on the card for one written reply in place of an answer per question
 * (formatQuestionsReply), which closes the round like Send answers; once answered, `words` shows it.
 * SF-T-227: with `onAnswer`, each question has a Skip button; a skipped one counts as answered and goes to
 * the agent as skipped, so a question the agent says may be left blank no longer holds up Send answers.
 * SF-T-308: the grill phase of the one decisions card: each question has the controls every decision has
 * (DecisionItemInput: options, the Other / steer box, Skip, Backlog), its detail, its chat (`askSlot`), and with
 * `canEnough` an Enough, build it that sends what's answered and tells the agent to build. A round's `progress`
 * (what's still open) shows under the header.
 */
export function FactoryQuestionsCard({
  questions,
  answered = false,
  closed = false,
  answers: given,
  words,
  meta,
  from = 'the AI builder',
  fold = false,
  id,
  onAnswer,
  onAnswers,
  sending = false,
  upstream,
  concerns,
  askSlot,
  canEnough = false,
  backlog,
}: {
  /** SF-T-308: what the AI builder reported for the questions answered Backlog, by number: the task it made, or why none. */
  backlog?: ReadonlyMap<number, BacklogOutcome>;
  questions: FactoryQuestions;
  /** SF-T-308: under each question, its live chat (SuggestionChat), answered or not. */
  askSlot?: (number: number, title: string, answered: boolean) => ReactNode;
  /** SF-T-308: offer Enough, build it (a run's questions on its task): the questions not answered go as skipped. */
  canEnough?: boolean;
  /** Who asked, in the header ("the agent" in a chat, T-51). */
  from?: string;
  /** A person has replied since (the timeline knows): the questions show as asked, not to answer. */
  answered?: boolean;
  /** SF-T-279: closed unanswered, as a person closed the run that asked: shown so, not as Answered. */
  closed?: boolean;
  /** Once answered: one answer per question, in order (parseFactoryAnswers), shown in place of the options. */
  answers?: (string | null)[] | null;
  /** SF-T-163: once answered with a reply in words, its text, shown after the questions. */
  words?: string | null;
  /** Who asked and when, at the end of the header line (the task's Decisions). */
  meta?: ReactNode;
  /** Starts folded to its header line, which opens it (T-72: an answered round in the task's Decisions). */
  fold?: boolean;
  /** For a pointer in the comments to scroll to it (decisionRoundId). */
  id?: string;
  /** Posts the answers (or a reply in words) as a chat message; leave out where nobody can answer (a preview). */
  onAnswer?: (message: string) => void;
  /**
   * SF-T-177: takes the answers as picked, one per question id, in place of `onAnswer`'s message (a coding
   * agent's own T3 question, answered through T3). No Reply in words: each question needs its own answer.
   */
  onAnswers?: (answers: Record<string, FactoryAnswer | undefined>) => void;
  /** With `onAnswers`: the answers are being sent (Send answers shows it). */
  sending?: boolean;
  upstream?: QuestionsUpstream;
  /** SF-T-182: Jev's Product or Architecture tags for the items (payloadConcerns of the message), a badge on each. */
  concerns?: ConcernTags;
}) {
  const [typed, setAnswers] = useState<Record<string, FactoryAnswer>>({});
  // SF-T-163: the Reply in words box; null while it's closed.
  const [reply, setReply] = useState<string | null>(null);
  // SF-T-308: Enough, build it was pressed: it goes once.
  const [ended, setEnded] = useState(false);
  const open = !answered && Boolean(onAnswer || onAnswers);
  // What the person chose here, else the answer picked from the replies upstream.
  const answers: Record<string, FactoryAnswer | undefined> = Object.fromEntries(
    questions.questions.map((q, i) => [q.id, typed[q.id] ?? proposalAnswer(q, upstream?.proposals.get(i + 1))])
  );
  const set = (id: string, change: Partial<FactoryAnswer>) =>
    setAnswers((prev) => {
      const was = prev[id] ?? answers[id] ?? { picked: [], other: '' };
      return { ...prev, [id]: { ...was, ...change } };
    });
  const complete = answersComplete(questions.questions, answers);
  // The answers go as one comment, cut short past TASK_CHAT_MAX (SF-T-265: an answer's lines are indented, so it grows).
  const message = !onAnswers && complete ? formatFactoryAnswers(questions.questions, answers) : null;
  const over = message ? message.length - TASK_CHAT_MAX : 0;
  // SF-T-227: T3's own questions (`onAnswers`) need an answer each, so only the chat path can skip (or Backlog, SF-T-308).
  const skippable = Boolean(onAnswer);
  const enoughMessage = canEnough && onAnswer && !onAnswers ? formatFactoryAnswers(questions.questions, answers, { enough: true }) : null;
  const sendable = !answered && upstream?.onSend ? questions.questions.flatMap((q, i) => (upstream.sent.has(i + 1) ? [] : [{ number: i + 1, title: q.header ? `${q.header}: ${q.question}` : q.question }])) : [];

  return (
    <FoldingCard
      id={id}
      fold={fold}
      summary={given ? answersSummary(questions.questions, given) : words ? `Replied: ${words.replace(/\s+/g, ' ')}` : closed ? 'The run that asked was closed' : undefined}
      header={
        <>
          <MessageCircleQuestion className="size-4" aria-hidden />
          {questions.questions.length === 1 ? `A question from ${from}` : `${questions.questions.length} questions from ${from}`}
          {closed ? <Badge variant="secondary">Closed, not answered</Badge> : answered ? <Badge variant="secondary">Answered</Badge> : null}
          {meta ? <span className="ml-auto font-normal">{meta}</span> : null}
        </>
      }
    >
      {questions.progress && open ? <p className="text-xs text-muted-foreground">{questions.progress}</p> : null}
      <ol className="space-y-3">
        {questions.questions.map((q, i) => (
          <li key={q.id} className="space-y-1.5">
            <p className="text-sm">
              <span className="text-muted-foreground">{i + 1}. </span>
              {q.header ? <span className="font-medium">{q.header}: </span> : null}
              {q.question} <ConcernBadge concerns={concerns} kind="question" item={i + 1} />
            </p>
            {q.detail ? <p className="whitespace-pre-wrap break-words pl-4 text-xs text-muted-foreground">{q.detail}</p> : null}
            {upstream && !answered && upstream.sent.has(i + 1) ? (
              <div className="pl-4">
                <SentUpstreamNote sourceLabel={upstream.sourceLabel} proposal={upstream.proposals.get(i + 1)} waiting={Boolean(upstream.waiting) && !sendReplyRead(upstream.sent.get(i + 1))}>
                  {q.options.length ? ownWordsNote(proposalAnswer(q, upstream.proposals.get(i + 1))) : undefined}
                </SentUpstreamNote>
              </div>
            ) : null}
            {open && reply === null ? (
              <QuestionInput question={q} answer={answers[q.id]} skippable={skippable} onChange={(change) => set(q.id, change)} />
            ) : given ? (
              <GivenAnswer answer={given[i] ?? null} outcome={backlog?.get(i + 1)} />
            ) : (
              <OptionList question={q} />
            )}
            {askSlot ? <div className="pl-4">{askSlot(i + 1, q.header ? `${q.header}: ${q.question}` : q.question, !open)}</div> : null}
          </li>
        ))}
      </ol>
      {words && !given ? (
        <div className="space-y-1 border-t pt-2">
          <p className="text-xs text-muted-foreground">Replied in words</p>
          <GivenAnswer answer={words} />
        </div>
      ) : null}
      {open && reply !== null ? (
        <div className="space-y-2">
          <Textarea
            aria-label="Your reply to the questions"
            placeholder="Answer or skip the questions in your own words…"
            rows={3}
            maxLength={QUESTIONS_REPLY_MAX}
            autoFocus
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setReply(null)}>
              Back to the questions
            </Button>
            <Button size="sm" disabled={!reply.trim()} onClick={() => onAnswer?.(formatQuestionsReply(reply))}>
              Send reply
            </Button>
          </div>
        </div>
      ) : open ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {sendable.length && upstream?.onSend ? (
            <span className="mr-auto">
              <SendUpstreamButton sourceLabel={upstream.sourceLabel} items={sendable} what="questions" tagging={upstream.tagging} onSend={upstream.onSend} />
            </span>
          ) : null}
          {onAnswer ? (
            <Button size="sm" variant="ghost" onClick={() => setReply('')}>
              Reply in words
            </Button>
          ) : null}
          {over > 0 ? (
            <span className="text-xs text-destructive">{`Too long to send: shorten the answers by ${formatNumber(over)} characters`}</span>
          ) : null}
          {!complete ? (
            <span className="text-xs text-muted-foreground">{skippable ? 'Answer, skip or Backlog each question to send' : 'Answer each question to send'}</span>
          ) : null}
          {enoughMessage !== null ? (
            <Button
              size="sm"
              variant="outline"
              disabled={ended || enoughMessage.length > TASK_CHAT_MAX}
              data-hint="Send what's answered; the AI builder stops asking, goes with its recommendation for the rest, and builds"
              onClick={() => {
                if (ended) return;
                setEnded(true);
                onAnswer?.(enoughMessage);
              }}
            >
              Enough, build it
            </Button>
          ) : null}
          <Button
            size="sm"
            disabled={!complete || over > 0 || ended}
            loading={sending}
            loadingText="Sending…"
            onClick={() => (onAnswers ? onAnswers(answers) : message !== null && onAnswer?.(message))}
          >
            Send answers
          </Button>
        </div>
      ) : null}
    </FoldingCard>
  );
}

/** T-72: the answers on one line for a folded round: "Scope: Only tasks it built · Where: On the task". */
function answersSummary(list: FactoryQuestion[], given: (string | null)[]): string | undefined {
  const parts = list.flatMap((q, i) => {
    const answer = isSkippedAnswer(given[i]) ? 'Skipped' : backlogAnswerNote(given[i]) !== null ? 'Backlog' : given[i]?.replace(/\s*\(Recommended\)/g, '').trim();
    return answer ? [q.header ? `${q.header}: ${answer}` : answer] : [];
  });
  return parts.length ? parts.join(' · ') : undefined;
}

/** One question's answer once sent: what was given, Skipped (SF-T-227), Backlog (SF-T-308), or No answer. */
function GivenAnswer({ answer, outcome }: { answer: string | null; outcome?: BacklogOutcome }) {
  const skipped = isSkippedAnswer(answer);
  const backlog = backlogAnswerNote(answer);
  const shown = skipped
    ? 'Skipped'
    : backlog !== null
      ? outcome
        ? 'task' in outcome
          ? `Backlog: the AI builder made ${outcome.task}`
          : `Backlog: the AI builder made no task (${outcome.reason})`
        : `Backlog: the AI builder makes a task of it${backlog ? ` (${backlog})` : ''}`
      : answer ?? 'No answer';
  return (
    <p className={cn('flex items-start gap-1.5 pl-4 text-sm', answer && !skipped && backlog === null ? 'font-medium' : 'text-muted-foreground')}>
      <CornerDownRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 whitespace-pre-line break-words">{shown}</span>
    </p>
  );
}

/**
 * T-22: in the comments, the AI builder's questions as one line pointing to the task's Decisions
 * (under the description), where they're answered and kept with their answers.
 */
export function FactoryQuestionsPointer({
  count,
  answered = false,
  closed = false,
  onShow,
}: {
  count: number;
  answered?: boolean;
  /** SF-T-279: closed unanswered (a person closed the run that asked). */
  closed?: boolean;
  /** Scrolls to the questions (showTaskDecisions). */
  onShow: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <MessageCircleQuestion className="size-4 shrink-0" aria-hidden />
      <span>{count === 1 ? 'Asked a question' : `Asked ${count} questions`}</span>
      {closed ? <Badge variant="secondary">Closed, not answered</Badge> : answered ? <Badge variant="secondary">Answered</Badge> : null}
      <Button variant="link" size="xs" className="h-auto p-0 text-xs" onClick={onShow}>
        {answered ? 'See Decisions' : 'Answer at the top'}
      </Button>
    </div>
  );
}

function OptionList({ question }: { question: FactoryQuestion }) {
  if (!question.options.length) return null;
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground marker:text-muted-foreground">
      {question.options.map((o) => (
        <li key={o.label}>
          <span className="text-foreground">{o.label}</span>
          {o.description ? ` — ${o.description}` : null}
        </li>
      ))}
    </ul>
  );
}

/** SF-T-308: a question's controls, as every decision has them (DecisionItemInput); Skip and Backlog only where the answers go as a message. */
function QuestionInput({
  question,
  answer,
  skippable = false,
  onChange,
}: {
  question: FactoryQuestion;
  answer: FactoryAnswer | undefined;
  /** SF-T-227: offer Skip (and, SF-T-308, Backlog). */
  skippable?: boolean;
  onChange: (change: Partial<FactoryAnswer>) => void;
}) {
  const choice = answer?.skipped ? 'skip' : answer?.backlog ? 'backlog' : null;
  return (
    <DecisionItemInput
      label={question.header ?? question.question}
      options={question.options}
      multiSelect={question.multiSelect}
      recommended={question.recommended}
      value={answer ? { picked: answer.picked, other: answer.other, choice } : undefined}
      actions={{ skip: skippable, backlog: skippable }}
      hints={{ skip: 'The AI builder goes with its recommendation', backlog: 'Left out of this build: the AI builder makes a task of it for later' }}
      placeholder={question.options.length ? 'Other, or steer it: “this, but …”' : 'Your answer…'}
      onChange={(change) =>
        onChange({
          ...(change.picked !== undefined ? { picked: change.picked } : {}),
          ...(change.other !== undefined ? { other: change.other } : {}),
          ...(change.choice !== undefined ? { skipped: change.choice === 'skip', backlog: change.choice === 'backlog' } : {}),
        })
      }
    />
  );
}
